import {
  getToken,
  getRefreshToken,
  saveSession,
  clearSession,
  markLogoutReason,
  ACTIVE_PORTAL_KEY,
  SESSION_UPDATED_EVENT,
} from "./sessionStorage";

// API base configuration Production
export const API_BASE_URL = "https://backend.sibolab.de";
// API base configuration Development
// export const API_BASE_URL = "http://localhost:8080";

const AUTH_ERROR_MESSAGES = new Set([
  "Invalid or expired token",
  "Missing or invalid Authorization header",
]);

// Roles are baked into the access token, so a role granted after login (e.g. a
// therapist application approved mid-session) is missing from it and the role
// check answers 403 "Forbidden". /auth/refresh re-reads roles from the DB, so
// one refresh + retry picks the new role up.
const ROLE_ERROR_MESSAGE = "Forbidden";

let refreshPromise = null;

async function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refreshToken = getRefreshToken();
      if (!refreshToken) throw new Error("No refresh token");

      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(localStorage.getItem(ACTIVE_PORTAL_KEY) ? { "X-Portal": localStorage.getItem(ACTIVE_PORTAL_KEY) } : {}),
        },
        body: JSON.stringify({ refreshToken }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw Object.assign(new Error(data.error || "Refresh failed"), { status: response.status });
      }

      saveSession(data.data.token, data.data.refreshToken, data.data.user);
      window.dispatchEvent(new CustomEvent(SESSION_UPDATED_EVENT, { detail: data.data.user }));
      return data.data.token;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// /auth/refresh answers 403 only for a deactivated account — sign out and
// tell the user why on the login page instead of a silent bounce.
function clearSessionAndRedirect(refreshError) {
  if (refreshError?.status === 403) markLogoutReason("inactive");
  clearSession();
  if (window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
}

const doFetch = (path, options, token) => {
  const isFormData = options.body instanceof FormData;
  return fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      // Omit Content-Type for FormData bodies — the browser must set it
      // itself so it can include the multipart boundary. Forcing
      // application/json here made the server try to JSON.parse a
      // multipart body and fail with "Unexpected token '-'".
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      "ngrok-skip-browser-warning": "true",
      // Which portal (admin / therapist / patient) the user is currently in —
      // lets the backend's usage log attribute calls from multi-role users.
      ...(localStorage.getItem(ACTIVE_PORTAL_KEY) ? { "X-Portal": localStorage.getItem(ACTIVE_PORTAL_KEY) } : {}),
      ...options.headers,
      Authorization: `Bearer ${token}`,
    },
  });
};

export async function apiFetch(path, options = {}) {
  let response = await doFetch(path, options, getToken());

  if (response.status === 401) {
    const body = await response
      .clone()
      .json()
      .catch(() => null);

    if (body && AUTH_ERROR_MESSAGES.has(body.error)) {
      let newToken;
      try {
        newToken = await refreshAccessToken();
      } catch (err) {
        clearSessionAndRedirect(err);
        return;
      }
      response = await doFetch(path, options, newToken);
    }
  } else if (response.status === 403) {
    const body = await response
      .clone()
      .json()
      .catch(() => null);

    if (body?.error === ROLE_ERROR_MESSAGE) {
      // The refresh also brings back the current roles: a newly granted role
      // makes the retry succeed; a removed one is applied by AuthProvider
      // (SESSION_UPDATED_EVENT), which moves the user off the page they lost.
      let newToken = null;
      try {
        newToken = await refreshAccessToken();
      } catch (err) {
        if (err.status === 403) {
          clearSessionAndRedirect(err);
          return;
        }
        // Network blip etc. — the session may still be valid, so keep the
        // original 403 rather than logging the user out over it.
      }
      if (newToken) {
        response = await doFetch(path, options, newToken);
      }
    }
  }

  const data = await response.json();
  if (!response.ok || data.success === false) {
    throw Object.assign(
      new Error(data.error || data.message || `HTTP error! Status: ${response.status}`),
      { status: response.status, ...(Array.isArray(data.errors) ? { errors: data.errors } : {}) }
    );
  }
  return data;
}
